import { cn } from "@-/com.npmjs.cn";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  Surface,
  SurfaceContent,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { getOfferedQuests } from "@-/dev.improve.haniverse.lib.quest.catalog";
import { useQuestPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.map.quest.navigator";
import { useMemo } from "react";
import { type QuestStatus, useQuestStatuses } from "#use-quest-statuses";

function MapQuestRow({
  isFirst,
  onOpen,
  status,
}: {
  isFirst: boolean;
  onOpen: () => void;
  status: QuestStatus;
}) {
  const { isCompleted, quest, unlock } = status;
  const isLocked = !unlock.isUnlocked;
  const detail = isLocked ? (unlock.label ?? "Locked") : isCompleted ? "Completed" : quest.summary;
  return (
    <Pressable
      accessibilityLabel={`${quest.title}. ${detail}`}
      accessibilityRole="button"
      accessibilityState={{ disabled: isLocked }}
      className={cn(
        "flex-row items-center gap-3 px-3 py-2.5 active:bg-accent",
        !isFirst && "border-t border-border/60",
      )}
      disabled={isLocked}
      onPress={onOpen}
      testID={`china-map-quest-${quest.id}`}
    >
      <View className="flex-1 gap-0.5">
        <Text
          className={cn(
            "text-base font-semibold",
            isLocked ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {quest.title}
        </Text>
        <Text className="text-xs text-muted-foreground" numberOfLines={2}>
          {detail}
        </Text>
      </View>
      {isLocked ? (
        <Icon className="text-muted-foreground" name="MdLockOutline" size={18} />
      ) : isCompleted ? (
        <Icon className="text-foreground" name="LuCheck" size={18} />
      ) : (
        <Text className="text-sm font-semibold text-foreground">+{quest.xp} XP</Text>
      )}
      {isLocked ? null : <Icon className="text-muted-foreground" name="MdChevronRight" size={20} />}
    </Pressable>
  );
}

/**
 * The quests offered where the learner is: global ones (the introductory quests) wherever that
 * is, a city's own only in that city. Locked quests are listed with what unlocks them.
 */
export function MapQuestsSurface({ currentAdcode }: { currentAdcode: number | null }) {
  const quests = useMemo(() => getOfferedQuests(currentAdcode), [currentAdcode]);
  const statuses = useQuestStatuses(quests);
  const questPageNavigator = useQuestPageNavigator();
  let content;
  if (statuses === undefined) {
    content = <ActivityIndicator className="py-3" />;
  } else if (statuses.length === 0) {
    content = (
      <Text className="px-3 py-2.5 text-sm text-muted-foreground">No missions here yet.</Text>
    );
  } else {
    content = statuses.map((status, index) => (
      <MapQuestRow
        isFirst={index === 0}
        key={status.quest.id}
        onOpen={() => questPageNavigator.push.mutate({ questId: status.quest.id })}
        status={status}
      />
    ));
  }
  return (
    <Surface testID="china-map-quests">
      <SurfaceHeader>
        <SurfaceTitle className="pb-2">Missions</SurfaceTitle>
      </SurfaceHeader>
      <SurfaceContent>{content}</SurfaceContent>
    </Surface>
  );
}
